import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { fmtMoney } from "../api";
import { TZ, fmtMonthYear, groupInt, parseServerDate } from "../lib/format";
import { cn } from "../lib/utils";
import { Button } from "./ui/button";

/** Sana bo'yicha guruh — master-detail chap paneli uchun. */
export type DateGroup<T> = {
  /** Mahalliy sana kaliti (YYYY-MM-DD), ko'rsatiluvchi sana bilan bir xil. */
  key: string;
  /** Guruhdagi birinchi yozuvning ISO vaqti (yorliq formatlash uchun). */
  repISO: string;
  total: number;
  items: T[];
};

/**
 * Yozuvlarni MAHALLIY sana bo'yicha guruhlaydi (ko'rsatiladigan sana bilan mos)
 * va eng yangisidan eskisiga tartiblaydi. `getISO` — created_at, `getAmount` —
 * summani qaytaradi.
 */
/**
 * Guruh kaliti — Toshkent vaqti bo'yicha "YYYY-MM-DD".
 *
 * Alohida eksport: hujjatga o'tishda uning kunini TOPISH kerak, va kalit
 * guruhlash bilan AYNAN bir xil qoidada hisoblanishi shart — aks holda
 * yarim tunga yaqin hujjat "topilmagan" bo'lib qolardi.
 */
export function dateGroupKey(iso: string): string {
  return parseServerDate(iso).toLocaleDateString("en-CA", { timeZone: TZ });
}

export function buildDateGroups<T>(
  items: T[],
  getISO: (x: T) => string,
  getAmount: (x: T) => number,
): DateGroup<T>[] {
  const map = new Map<string, DateGroup<T>>();
  for (const it of items) {
    const iso = getISO(it);
    const key = dateGroupKey(iso);
    const cur = map.get(key);
    if (cur) {
      cur.items.push(it);
      cur.total += getAmount(it);
    } else {
      map.set(key, { key, repISO: iso, total: getAmount(it), items: [it] });
    }
  }
  // Kalit YYYY-MM-DD — leksikografik kamayuvchi = eng yangisidan.
  return [...map.values()].sort((a, b) => (a.key < b.key ? 1 : a.key > b.key ? -1 : 0));
}

/**
 * Master-detail chap paneli: sanalar ro'yxati (eng yangisidan), har biri kunlik
 * jami + hujjatlar soni bilan. App sidebar'idan alohida, o'z scroll zonasi.
 */
export function DateColumn({
  groups,
  selectedKey,
  onSelect,
  labelOf,
  countLabel,
  hasMore,
  onLoadMore,
  loadingMore,
  loadMoreLabel,
  totalLabel,
}: {
  groups: { key: string; total: number; count: number; repISO: string; debt?: number }[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
  labelOf: (repISO: string) => string;
  countLabel: (n: number) => string;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  loadMoreLabel?: string;
  totalLabel?: string;
}) {
  // Kunlar bir oydan oshsa — yig'iladigan oy bo'limlari. `groups` eng
  // yangisidan tartiblangani uchun oylar ketma-ket chiqadi.
  const sections = useMemo(() => {
    const out: { monthKey: string; days: typeof groups; count: number; total: number }[] = [];
    for (const g of groups) {
      const monthKey = g.key.slice(0, 7);
      const last = out[out.length - 1];
      if (last && last.monthKey === monthKey) {
        last.days.push(g);
        last.count += g.count;
        last.total += g.total;
      } else {
        out.push({ monthKey, days: [g], count: g.count, total: g.total });
      }
    }
    return out;
  }, [groups]);
  const multiMonth = sections.length > 1;

  // Foydalanuvchining aniq tanlovi (yo'q bo'lsa default amal qiladi) — "ko'proq
  // yuklash" ro'yxatni o'zgartirganda ochiq oylar yopilib qolmasligi uchun.
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const newestMonth = sections[0]?.monthKey;
  const selectedMonth = selectedKey ? selectedKey.slice(0, 7) : null;
  const isExpanded = (mk: string) =>
    overrides[mk] ?? (mk === newestMonth || mk === selectedMonth);

  // Tanlov eski oyga sakraganda (masalan, hujjatga o'tish) uni albatta ochamiz —
  // foydalanuvchi keyin qo'lda yopishi mumkin.
  useEffect(() => {
    if (!selectedMonth) return;
    setOverrides((o) =>
      o[selectedMonth] === false ? { ...o, [selectedMonth]: true } : o,
    );
  }, [selectedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const dayButton = (g: (typeof groups)[number]) => {
    const active = g.key === selectedKey;
    return (
      <button
        key={g.key}
        type="button"
        onClick={() => onSelect(g.key)}
        className={cn(
          "date-ledger-day flex w-full flex-col gap-0.5 px-3 py-2.5 text-left transition-colors",
          active && "is-active",
          active ? "bg-primary/10" : "hover:bg-accent",
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <span className={cn("text-sm font-medium", active && "text-primary")}>
            {labelOf(g.repISO)}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground">
            {countLabel(g.count)}
          </span>
        </div>
        <span className="text-sm font-semibold tabular-nums">{totalLabel ? `${totalLabel}: ` : ""}{fmtMoney(g.total)}</span>
        {g.debt != null && g.debt > 0 && (
          <span className="text-xs font-medium tabular-nums text-destructive">
            Qarz: {fmtMoney(g.debt)}
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="date-ledger flex max-h-[70vh] flex-col overflow-hidden rounded-lg border bg-card md:max-h-[calc(100vh-12rem)]">
      <div className="min-h-0 flex-1 overflow-y-auto divide-y">
        {!multiMonth
          ? groups.map(dayButton)
          : sections.map((s) => {
              const expanded = isExpanded(s.monthKey);
              return (
                <Fragment key={s.monthKey}>
                  <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() =>
                      setOverrides((o) => ({ ...o, [s.monthKey]: !expanded }))
                    }
                    className="date-ledger-month flex w-full items-center gap-2 bg-muted/50 px-3 py-2 text-left transition-colors hover:bg-accent"
                  >
                    <ChevronDown
                      aria-hidden
                      className={cn(
                        "size-4 shrink-0 text-muted-foreground transition-transform",
                        !expanded && "-rotate-90",
                      )}
                    />
                    <span className="text-sm font-semibold">
                      {fmtMonthYear(s.monthKey)}
                    </span>
                    <span className="ml-auto shrink-0 text-right">
                      <span className="block text-xs text-muted-foreground">
                        {countLabel(s.count)}
                      </span>
                      <span className="block text-xs font-semibold tabular-nums">
                        {fmtMoney(s.total)}
                      </span>
                    </span>
                  </button>
                  {expanded && s.days.map(dayButton)}
                </Fragment>
              );
            })}
      </div>
      {hasMore && onLoadMore && (
        <div className="flex-none border-t p-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="w-full"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadMoreLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

export type LedgerLine = {
  product_nomi: string;
  miqdor: string;
  birlik_narx: string;
  jami_summa: string;
};

export type LedgerDoc = {
  id: number;
  /** Yetkazib beruvchi yoki mijoz nomi (hujjat sarlavhasi). */
  party: string;
  /**
   * Hujjat qaysi filialga tegishli. Faqat `headers.branch` berilganda
   * ko'rinadi (bir nechta filiali bor ustaxona) — `foyda` ustuni bilan bir xil
   * naqsh.
   */
  branch?: ReactNode;
  timeISO: string;
  /** To'lov / holat belgisi (party katakcha ostida ko'rinadi). */
  badge: ReactNode;
  /**
   * To'lov USULI — belgining ostida, o'sha to'lov katagida. ATAYLAB alohida
   * ustun emas: yig'ilgan qatorlar allaqachon zich va usul egasi ko'z
   * yugurtiradigan narsa emas, u hujjatga qaraganda kerak bo'ladi.
   */
  method?: ReactNode;
  /** Hujjat jami summasi. */
  total: number | string;
  remaining?: string;
  economics?: React.ReactNode;
  /** Hujjat bo'yicha to'langan summa. */
  paid: number | string;
  /** Foyda — faqat sotuvda; xaridda berilmaydi (ustun yashiriladi). */
  profit?: number | string | null;
  cancelled?: boolean;
  /**
   * Jurnaldan shu hujjatga o'tildi — ajratib ko'rsatiladi va ko'rinishga
   * suriladi. Aks holda egasi 200 qatorli kundan o'zi qidirishi kerak bo'lardi.
   */
  highlighted?: boolean;
  lines: LedgerLine[];
  /** O'zgartirish / bekor qilish tugmalari (amal ustuni). */
  actions?: ReactNode;
};

export type LedgerHeaders = {
  party: string;
  tovar: string;
  narx: string;
  miqdor: string;
  jami: string;
  tolangan: string;
  /** Berilsa foyda ustuni ko'rsatiladi (sotuv). */
  foyda?: string;
  /** Berilsa filial ustuni ko'rsatiladi (bir nechta filialli ustaxona). */
  branch?: string;
  vaqt: string;
  amal: string;
};

/**
 * Tanlangan kunning batafsil ko'rinishi — hujjatlar bo'yicha guruhlangan TO'LIQ
 * jadval. Tovar/narx/miqdor satr darajasida; mijoz, jami, to'langan, foyda,
 * vaqt va amal esa hujjat darajasida (`rowSpan` bilan satrlar bo'ylab birlashadi)
 * — ko'p tovarli hujjatda ham pul ustunlari bir marta ko'rinadi.
 */
export function LedgerDayTable({
  docs,
  headers,
  qarzLabel,
}: {
  docs: LedgerDoc[];
  headers: LedgerHeaders;
  /** Qoldiq (jami > to'langan) yorlig'i, masalan "Qarz". */
  qarzLabel?: string;
}) {
  const showFoyda = headers.foyda !== undefined;
  const showBranch = headers.branch !== undefined;
  // Qaysi hujjatga surilgani. Ref shart: ref-callback har renderda qaytadan
  // chaqiriladi, qorovulsiz esa foydalanuvchi qo'lda pastga surganda har
  // qanday render uni yana yuqoriga tortib qaytarardi.
  const scrolledFor = useRef<number | null>(null);
  return (
    <div className="overflow-x-auto">
      <table
        className={cn("w-full text-sm", showBranch ? "min-w-[950px]" : "min-w-[820px]")}
      >
        <colgroup>
          <col className="w-[150px]" />
          <col />
          <col className="w-[104px]" />
          <col className="w-[70px]" />
          <col className="w-[116px]" />
          <col className="w-[120px]" />
          {showFoyda && <col className="w-[104px]" />}
          {showBranch && <col className="w-[130px]" />}
          <col className="w-[56px]" />
          {/* Amallar: 3 tagacha ikonka (to'lov, bekor, qaytarish) sig'adi. */}
          <col className="w-[120px]" />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-muted/95 backdrop-blur">
          <tr className="border-b text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-2 text-left">{headers.party}</th>
            <th className="px-2 py-2 text-left">{headers.tovar}</th>
            <th className="px-2 py-2 text-right">{headers.narx}</th>
            <th className="px-2 py-2 text-right">{headers.miqdor}</th>
            <th className="px-2 py-2 text-right">{headers.jami}</th>
            <th className="px-2 py-2 text-right">{headers.tolangan}</th>
            {showFoyda && <th className="px-2 py-2 text-right">{headers.foyda}</th>}
            {showBranch && <th className="px-2 py-2 text-left">{headers.branch}</th>}
            <th className="px-2 py-2 text-right">{headers.vaqt}</th>
            <th className="px-2 py-2 text-center">{headers.amal}</th>
          </tr>
        </thead>
        <tbody>
          {docs.map((doc, docIndex) => {
            const time = parseServerDate(doc.timeISO).toLocaleTimeString("uz-UZ", {
              timeZone: TZ,
              hour: "2-digit",
              minute: "2-digit",
            });
            const lines = doc.lines.length ? doc.lines : [null];
            const span = lines.length;
            const qarz = doc.remaining != null ? Number(doc.remaining) : Number(doc.total) - Number(doc.paid);
            return (
              <Fragment key={doc.id}>
                {lines.map((l, i) => (
                  <tr
                    key={i}
                    // Faqat birinchi satrga va faqat BIR MARTA suriladi —
                    // hujjat boshidan ko'rinsin.
                    ref={
                      i === 0 && doc.highlighted
                        ? (el) => {
                            if (el && scrolledFor.current !== doc.id) {
                              scrolledFor.current = doc.id;
                              el.scrollIntoView({ block: "center", behavior: "smooth" });
                            }
                          }
                        : undefined
                    }
                    className={cn(
                      "border-t",
                      i === 0 && (docIndex > 0 ? "border-t-4 border-t-muted-foreground/30" : "border-t-2"),
                      doc.cancelled && "opacity-50",
                      doc.highlighted && "bg-primary/10",
                    )}
                  >
                    {i === 0 && (
                      <td rowSpan={span} className="px-3 py-2 align-top">
                        <div className="font-medium leading-tight">{doc.party}</div>
                        {doc.economics}
                        <div className="mt-1">{doc.badge}</div>
                        {doc.method && (
                          <div className="mt-1 text-xs">{doc.method}</div>
                        )}
                      </td>
                    )}
                    {l ? (
                      <>
                        <td className="px-2 py-1.5">{l.product_nomi}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {groupInt(l.birlik_narx)}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {Number(l.miqdor)}
                        </td>
                      </>
                    ) : (
                      <td colSpan={3} className="px-2 py-1.5 text-muted-foreground">
                        —
                      </td>
                    )}
                    {i === 0 && (
                      <>
                        <td
                          rowSpan={span}
                          className="px-2 py-2 text-right align-top font-semibold tabular-nums"
                        >
                          {groupInt(doc.total)}
                        </td>
                        <td
                          rowSpan={span}
                          className="px-2 py-2 text-right align-top tabular-nums"
                        >
                          <div>{groupInt(doc.paid)}</div>
                          {qarz > 0 && (
                            <div className="text-xs text-destructive">
                              {qarzLabel ? `${qarzLabel}: ` : ""}
                              {groupInt(qarz)}
                            </div>
                          )}
                        </td>
                        {showFoyda && (
                          <td
                            rowSpan={span}
                            className="px-2 py-2 text-right align-top font-medium tabular-nums text-success"
                          >
                            {doc.profit === null || doc.profit === undefined
                              ? "—"
                              : groupInt(doc.profit)}
                          </td>
                        )}
                        {showBranch && (
                          <td
                            rowSpan={span}
                            className="px-2 py-2 align-top text-xs text-muted-foreground"
                          >
                            {doc.branch ?? "—"}
                          </td>
                        )}
                        <td
                          rowSpan={span}
                          className="px-2 py-2 text-right align-top text-xs text-muted-foreground tabular-nums"
                        >
                          {time}
                        </td>
                        <td rowSpan={span} className="px-2 py-2 text-center align-top">
                          {doc.actions}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
